import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api";

const inputCls =
  "w-full bg-cream border border-chocolate/15 px-4 py-3 text-sm text-chocolate placeholder:text-chocolate/35 focus:outline-none focus:border-gold transition-colors rounded-none";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-chocolate/60 mb-2";

const STEPS = ["The Recipient", "Their World", "The Occasion", "About You"];

const Select = ({ label, id, testid, value, onChange, options, placeholder }) => (
  <div>
    <label className={labelCls} htmlFor={id}>{label}</label>
    <select id={id} data-testid={testid} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </div>
);

const Field = ({ label, id, testid, value, onChange, placeholder, textarea, type = "text" }) => (
  <div>
    <label className={labelCls} htmlFor={id}>{label}</label>
    {textarea ? (
      <textarea id={id} data-testid={testid} rows={4} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    ) : (
      <input id={id} data-testid={testid} type={type} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    )}
  </div>
);

export default function GiftEditForm() {
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState({
    recipient: "", relationship: "", age_range: "",
    interests: "", personality: "", avoid: "",
    occasion: "", date_needed: "", budget: "", wrapping: "",
    pathway: "Easy Edit", name: "", email: "", phone: "", contact_method: "", notes: "",
  });

  const set = (key) => (value) => setData((d) => ({ ...d, [key]: value }));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!data.name.trim() || !data.email.trim()) {
      toast.error("Please add your name and email so we can reply with your proposal.");
      return;
    }
    setSending(true);
    try {
      await submitEnquiry("gift-edit", data);
      toast.success("Your brief is with our curators — expect a personal reply within one working day.");
      setData({
        recipient: "", relationship: "", age_range: "", interests: "", personality: "", avoid: "",
        occasion: "", date_needed: "", budget: "", wrapping: "", pathway: "Easy Edit",
        name: "", email: "", phone: "", contact_method: "", notes: "",
      });
      setStep(0);
    } catch {
      toast.error("Something went wrong sending your brief. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="gift-edit-brief-form" className="bg-ivory border border-chocolate/10 p-6 sm:p-10">
      <div className="flex items-center justify-between mb-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-golddark">
          Step {String(step + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
        </p>
        <p className="font-serif italic text-lg text-chocolate/70">{STEPS[step]}</p>
      </div>
      <div className="h-px bg-chocolate/10 relative mb-8">
        <motion.div
          className="absolute left-0 top-0 h-px bg-gold"
          animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="space-y-5 min-h-[300px]"
        >
          {step === 0 && (
            <>
              <Field label="Who is the gift for?" id="ge-recipient" testid="brief-recipient" value={data.recipient} onChange={set("recipient")} placeholder="e.g. My sister, Daniel, our nanny…" />
              <div className="grid sm:grid-cols-2 gap-5">
                <Select label="Relationship" id="ge-rel" testid="brief-relationship" value={data.relationship} onChange={set("relationship")}
                  options={["Partner", "Family", "Friend", "Colleague", "Client", "Other"]} />
                <Select label="Age range" id="ge-age" testid="brief-age" value={data.age_range} onChange={set("age_range")}
                  options={["Under 18", "18–29", "30–45", "46–60", "60+"]} />
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <Field label="Their interests & passions" id="ge-interests" testid="brief-interests" textarea value={data.interests} onChange={set("interests")}
                placeholder="Ceramics, long-distance running, 70s vinyl, very spoilt dachshund…" />
              <Field label="Three words that describe them" id="ge-personality" testid="brief-personality" value={data.personality} onChange={set("personality")} placeholder="Warm, witty, impossible to buy for" />
              <Field label="Anything to avoid?" id="ge-avoid" testid="brief-avoid" value={data.avoid} onChange={set("avoid")} placeholder="Allergies, dislikes, things they already own…" />
            </>
          )}
          {step === 2 && (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <Select label="Occasion" id="ge-occasion" testid="brief-occasion" value={data.occasion} onChange={set("occasion")}
                  options={["Birthday", "Anniversary", "Thank you", "New baby", "Wedding", "Corporate", "Just because", "Other"]} />
                <Field label="Date needed by" id="ge-date" testid="brief-date" type="date" value={data.date_needed} onChange={set("date_needed")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Select label="Budget guide" id="ge-budget" testid="brief-budget" value={data.budget} onChange={set("budget")}
                  options={["£50 – £100", "£100 – £250", "£250 – £500", "£500 – £1,000", "£1,000+"]} />
                <Select label="Wrapping style" id="ge-wrap" testid="brief-wrapping" value={data.wrapping} onChange={set("wrapping")}
                  options={["Signature ivory & gold", "Sage & linen", "Deep chocolate & ribbon", "Curator's choice"]} />
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Your name *" id="ge-name" testid="brief-name" value={data.name} onChange={set("name")} placeholder="Jane Appleton" />
                <Field label="Your email *" id="ge-email" testid="brief-email" type="email" value={data.email} onChange={set("email")} placeholder="jane@email.com" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Phone (optional)" id="ge-phone" testid="brief-phone" type="tel" value={data.phone} onChange={set("phone")} placeholder="+44 …" />
                <Select label="Preferred contact" id="ge-contact" testid="brief-contact-method" value={data.contact_method} onChange={set("contact_method")}
                  options={["Email", "Phone call", "Text / WhatsApp"]} />
              </div>
              <Field label="Anything else we should know?" id="ge-notes" testid="brief-notes" textarea value={data.notes} onChange={set("notes")} placeholder="Delivery address hints, in-jokes, the story behind the gift…" />
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-chocolate/10">
        <button
          type="button"
          data-testid="brief-back-button"
          onClick={back}
          disabled={step === 0}
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-chocolate/60 hover:text-chocolate transition-colors disabled:opacity-0"
        >
          <ArrowLeft size={14} /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button
            type="button"
            data-testid="brief-next-button"
            onClick={next}
            className="inline-flex items-center gap-2 bg-chocolate text-cream px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-espresso transition-colors"
          >
            Continue <ArrowRight size={14} />
          </button>
        ) : (
          <button
            type="button"
            data-testid="brief-form-submit-button"
            onClick={submit}
            disabled={sending}
            className="inline-flex items-center gap-2 bg-gold text-espresso px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-champagne transition-colors disabled:opacity-60"
          >
            {sending ? "Sending…" : "Send My Brief"} <ArrowRight size={14} />
          </button>
        )}
      </div>
      <p className="text-[11px] text-chocolate/45 mt-4 leading-relaxed">
        There are no fixed prices for bespoke edits. We'll respond with a curated proposal and agree the price with you before any payment is taken.
      </p>
    </div>
  );
}
